import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Chip, IconButton, Stack, TextField } from '@mui/material';
import { OfferAudioUploadState, OfferAudioVariantConfig } from '../types';
import { hasVariantRequiredFieldErrors } from '../utils';
import AudioUploader from './AudioUploader';

type VariantCardProps = {
    variant: OfferAudioVariantConfig;
    uploadState?: OfferAudioUploadState;
    canEdit: boolean;
    stateKey: string;
    onBoxNameChange: (value: string) => void;
    onPackageIdChange: (value: string) => void;
    onFileUpload: (file: File) => void;
    onRemove: () => void;
};

export default function VariantCard({
    variant,
    uploadState,
    canEdit,
    stateKey,
    onBoxNameChange,
    onPackageIdChange,
    onFileUpload,
    onRemove,
}: VariantCardProps) {
    const boxNameError = variant.box_name.trim().length === 0;
    const packageError = variant.package_id.trim().length === 0;
    const audioUrlError = (variant.audioUrl || variant.audio_file || '').trim().length === 0;
    const hasRequiredFieldError = hasVariantRequiredFieldErrors(variant);

    return (
        <div className={`oz-offer-language-config-variant-card ${hasRequiredFieldError ? 'is-error' : ''}`}>
            <div className="oz-offer-language-config-variant-header">
                <TextField
                    value={variant.box_name}
                    onChange={(e) => onBoxNameChange(e.target.value)}
                    disabled={!canEdit}
                    placeholder="Box name"
                    error={boxNameError}
                    helperText={boxNameError ? 'Box name is required.' : false}
                    size="small"
                    variant="standard"
                    InputProps={{
                        disableUnderline: !canEdit,
                        sx: { fontSize: '16px', fontWeight: 500, color: '#0f172a' },
                    }}
                    sx={{ flex: 1, minWidth: 0 }}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                    {uploadState?.status === 'success' && <Chip size="small" label="Uploaded" color="success" variant="outlined" />}
                    {uploadState?.status === 'uploading' && <Chip size="small" label="Uploading" color="info" variant="outlined" />}
                    {uploadState?.status === 'error' && <Chip size="small" label="Upload failed" color="error" variant="outlined" />}
                    {canEdit && (
                        <IconButton size="small" onClick={onRemove} sx={{ color: '#94a3b8' }}>
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            </div>

            <label className="oz-offer-language-config-field-label" htmlFor={`${stateKey}-package`}>
                Package ID
            </label>
            <TextField
                id={`${stateKey}-package`}
                value={variant.package_id}
                onChange={(e) => onPackageIdChange(e.target.value)}
                disabled={!canEdit}
                fullWidth
                placeholder="Enter Package ID"
                error={packageError}
                helperText={packageError ? 'Package ID is required.' : false}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />

            <AudioUploader
                variant={variant}
                uploadState={uploadState}
                canEdit={canEdit}
                onFileUpload={onFileUpload}
                stateKey={stateKey}
                audioUrlError={audioUrlError}
            />
        </div>
    );
}
